import { Link } from "react-router-dom";
import { AlertTriangle, BookOpen, ShieldCheck, Asterisk, Check, CircleDashed } from "lucide-react";
import { RISK_POINTS, CANONICAL_SOURCES, DILIGENCE_CHECKLIST, COMPARISON_ROWS, PACKAGES, QV_TABLE } from "../content/site";

export const InShort = ({ children }) => (
  <aside data-testid="in-short-box" className="border-l-2 pl-5 py-1 my-8" style={{ borderColor: "var(--accent)" }}>
    <p className="text-xs uppercase tracking-[0.2em] mb-2 font-medium" style={{ color: "var(--accent)" }}>In short</p>
    <p className="text-lg leading-relaxed" style={{ color: "var(--ink)" }}>{children}</p>
  </aside>
);

export const DiligenceBox = ({ title = "What to verify", items }) => (
  <aside data-testid="diligence-box" className="my-8 p-6 border-l-4" style={{ background: "var(--diligence-bg)", borderColor: "var(--bronze)" }}>
    <div className="flex items-center gap-2 mb-3">
      <ShieldCheck size={18} style={{ color: "var(--bronze)" }} />
      <p className="text-xs uppercase tracking-[0.2em] font-medium" style={{ color: "var(--bronze)" }}>{title}</p>
    </div>
    <ul className="space-y-2 m-0 p-0 list-none">
      {items.map((item, i) => (
        <li key={i} className="text-base leading-relaxed flex gap-2" style={{ color: "var(--ink)" }}>
          <span style={{ color: "var(--bronze)" }}>·</span>
          <span>{item}</span>
        </li>
      ))}
    </ul>
  </aside>
);

export const RiskBox = () => (
  <aside data-testid="risk-box" className="my-10 p-6 border" style={{ background: "var(--diligence-bg)", borderColor: "var(--line)" }}>
    <div className="flex items-center gap-2 mb-3">
      <AlertTriangle size={18} style={{ color: "var(--bronze)" }} />
      <p className="text-xs uppercase tracking-[0.2em] font-medium" style={{ color: "var(--bronze)" }}>Risk, plainly</p>
    </div>
    <ul className="space-y-1.5 m-0 p-0 list-none">
      {RISK_POINTS.map((point, i) => (
        <li key={i} className="text-sm leading-relaxed flex gap-2" style={{ color: "var(--ink-muted)" }}>
          <span style={{ color: "var(--bronze)" }}>·</span>
          <span>{point}</span>
        </li>
      ))}
    </ul>
  </aside>
);

export const Note = ({ children }) => (
  <p className="text-sm italic my-6 pl-4 border-l" style={{ color: "var(--ink-muted)", borderColor: "var(--line)" }}>{children}</p>
);

const people = [
  { name: "Emmanuel Quezada", role: "Founder", bio: "Per the official deck: entrepreneur since 15, first app at 16, 11+ years in blockchain; founded U-TOPIA on 16 October 2021; recognized in Forbes 30 Under 30 Portugal. From the family behind ATS Group, a compliance consulting firm." },
  { name: "Kyle Kemper", role: "President", bio: "Tech and wallets background; in crypto since 2013. Author of The Unified Wallet: Unlocking the Digital Golden Age." },
  { name: "Maya Nicks", role: "Head of AI / acting CTO", bio: "AI engineer leading the technical vision (the person to watch on the product's AI direction." },
  { name: "Sean Walsh", role: "CMO", bio: "Leads marketing across the ecosystem." },
  { name: "Zac Bertram", role: "Community voice", bio: "Daily content, discovery calls, and network support) the most visible day-to-day presence for members." },
];

export const PeopleGrid = () => (
  <div className="grid gap-4 my-8 sm:grid-cols-2">
    {people.map((p, i) => (
      <div key={i} data-testid={`person-card-${i}`} className="p-6 border card-lift" style={{ background: "var(--card)", borderColor: "var(--line)" }}>
        <p className="font-serif-display text-xl mb-1" style={{ color: "var(--ink)" }}>{p.name}</p>
        <p className="text-xs uppercase tracking-[0.15em] mb-3 font-medium" style={{ color: "var(--accent)" }}>{p.role}</p>
        <p className="text-sm leading-relaxed m-0" style={{ color: "var(--ink-muted)" }}>{p.bio}</p>
      </div>
    ))}
  </div>
);

export const ComparisonTable = () => (
  <div className="overflow-x-auto my-8">
    <table data-testid="comparison-table" className="w-full text-left border-collapse" style={{ minWidth: 640 }}>
      <thead>
        <tr style={{ borderBottom: "2px solid var(--ink)" }}>
          <th className="py-3 pr-4 text-xs uppercase tracking-[0.15em] font-medium" style={{ color: "var(--ink-muted)" }}></th>
          <th className="py-3 pr-4 font-serif-display text-xl font-medium" style={{ color: "var(--ink)" }}>U.CENTER</th>
          <th className="py-3 font-serif-display text-xl font-medium" style={{ color: "var(--ink)" }}>U-TOPIA</th>
        </tr>
      </thead>
      <tbody>
        {COMPARISON_ROWS.map((row, i) => (
          <tr key={i} style={{ borderBottom: "1px solid var(--line)" }}>
            <td className="py-4 pr-4 text-xs uppercase tracking-[0.15em] font-medium align-top" style={{ color: "var(--ink-muted)" }}>{row[0]}</td>
            <td className="py-4 pr-4 text-base align-top" style={{ color: "var(--ink)" }}>{row[1]}</td>
            <td className="py-4 text-base align-top" style={{ color: "var(--ink)" }}>{row[2]}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export const QvTable = () => (
  <div className="my-8">
    <div data-testid="qv-table" className="grid grid-cols-2 sm:grid-cols-4 gap-px border" style={{ background: "var(--line)", borderColor: "var(--line)" }}>
      {QV_TABLE.map(([rank, qv], i) => (
        <div key={i} className="p-4" style={{ background: "var(--card)" }}>
          <p className="text-xs uppercase tracking-[0.15em] font-medium m-0" style={{ color: "var(--ink-muted)" }}>{rank}</p>
          <p className="font-serif-display text-lg m-0" style={{ color: "var(--ink)" }}>{qv}</p>
        </div>
      ))}
    </div>
    <p className="text-xs mt-3 m-0" style={{ color: "var(--ink-muted)" }}>Verified against U.CENTER's official compensation one-pager, September 2026. QV is cumulative: never flushed, only added.</p>
  </div>
);

export const PackagesTable = () => (
  <div className="overflow-x-auto my-8">
    <table data-testid="packages-table" className="w-full text-left border-collapse" style={{ minWidth: 640 }}>
      <thead>
        <tr style={{ borderBottom: "2px solid var(--ink)" }}>
          {["Package", "Price", "Points", "What stands out"].map((h) => (
            <th key={h} className="py-3 pr-4 text-xs uppercase tracking-[0.15em] font-medium" style={{ color: "var(--ink-muted)" }}>{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {PACKAGES.map((row, i) => (
          <tr key={i} style={{ borderBottom: "1px solid var(--line)" }}>
            <td className="py-3.5 pr-4 font-serif-display text-lg" style={{ color: "var(--ink)" }}>{row[0]}</td>
            <td className="py-3.5 pr-4 text-base" style={{ color: "var(--ink)" }}>{row[1]}</td>
            <td className="py-3.5 pr-4 text-base font-medium" style={{ color: "var(--accent)" }}>{row[2]}</td>
            <td className="py-3.5 text-sm" style={{ color: "var(--ink-muted)" }}>{row[3]}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export const Exhibit = ({ src, alt, caption, label = "Exhibit", tid }) => (
  <figure data-testid={tid || "exhibit"} className="my-10">
    <div className="relative overflow-hidden border card-lift" style={{ borderColor: "var(--line)", background: "var(--card)", clipPath: "polygon(0 0, 100% 0, 100% calc(100% - 26px), calc(100% - 26px) 100%, 0 100%)" }}>
      <div aria-hidden="true" className="absolute inset-0 pointer-events-none z-10" style={{ background: "radial-gradient(65% 45% at 50% 0%, rgba(47,93,80,0.10), transparent 72%)" }} />
      <img src={src} alt={alt} loading="lazy" className="w-full block" />
    </div>
    <figcaption className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1 mt-3 text-xs" style={{ color: "var(--ink-muted)" }}>
      <span className="uppercase tracking-[0.22em] font-medium shrink-0" style={{ color: "var(--accent)" }}>{label}</span>
      <span className="text-right flex-1 min-w-[200px]">{caption}</span>
    </figcaption>
  </figure>
);

export const Checklist = () => (
  <ol className="my-8 p-0 list-none space-y-0 border-t" style={{ borderColor: "var(--line)" }}>
    {DILIGENCE_CHECKLIST.map((item, i) => (
      <li key={i} data-testid={`checklist-item-${i}`} className="flex gap-5 py-5 border-b" style={{ borderColor: "var(--line)" }}>
        <span className="chapter-numeral text-4xl shrink-0 w-12">{String(i + 1).padStart(2, "0")}</span>
        <div>
          <p className="font-medium m-0 mb-1" style={{ color: "var(--ink)" }}>{item.title}</p>
          <p className="text-sm m-0 leading-relaxed" style={{ color: "var(--ink-muted)" }}>{item.body}</p>
        </div>
      </li>
    ))}
  </ol>
);

export const SourcesList = () => (
  <ul className="my-8 p-0 list-none space-y-3">
    {CANONICAL_SOURCES.map((s, i) => (
      <li key={i}>
        <a data-testid={`source-link-${i}`} href={s.href} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 p-4 border card-lift no-underline" style={{ background: "var(--card)", borderColor: "var(--line)" }}>
          <BookOpen size={16} style={{ color: "var(--accent)" }} className="shrink-0" />
          <span className="text-base" style={{ color: "var(--ink)" }}>{s.label}</span>
        </a>
      </li>
    ))}
  </ul>
);

export const GlossaryList = ({ items }) => (
  <dl className="my-8 border-t" style={{ borderColor: "var(--line)" }}>
    {items.map((item, i) => (
      <div key={i} className="py-5 border-b grid gap-1 sm:grid-cols-[220px_1fr] sm:gap-6" style={{ borderColor: "var(--line)" }}>
        <dt className="font-medium" style={{ color: "var(--slate)" }}>{item.term}</dt>
        <dd className="m-0 text-base leading-relaxed" style={{ color: "var(--ink-muted)" }}>{item.def}</dd>
      </div>
    ))}
  </dl>
);

export const Marquee = ({ items }) => {
  const row = (
    <div className="flex items-center shrink-0">
      {items.map((item, i) => (
        <span key={i} className="flex items-center gap-8 pr-8 text-sm uppercase tracking-[0.25em] whitespace-nowrap" style={{ color: "var(--ink-muted)" }}>
          {item}
          <Asterisk size={14} style={{ color: "var(--accent)" }} />
        </span>
      ))}
    </div>
  );
  return (
    <div data-testid="editorial-marquee" className="overflow-hidden py-4 border-y select-none" style={{ borderColor: "var(--line)", background: "var(--card)" }} aria-hidden="true">
      <div className="marquee-track">
        {row}
        {row}
      </div>
    </div>
  );
};

export const RelatedLinks = ({ links }) => (
  <nav className="mt-12 pt-8 border-t" style={{ borderColor: "var(--line)" }} aria-label="Related pages">
    <p className="text-xs uppercase tracking-[0.2em] mb-4 font-medium" style={{ color: "var(--ink-muted)" }}>Keep reading</p>
    <div className="flex flex-wrap gap-3">
      {links.map((l, i) => (
        <Link key={i} data-testid={`related-link-${i}`} to={l.to} className="outline-btn outline-btn--accent no-underline">{l.label}</Link>
      ))}
    </div>
  </nav>
);

export const VerdictBox = ({ verdict, verified = [], open = [] }) => (
  <aside data-testid="verdict-box" className="my-12 p-7 md:p-9 border" style={{ background: "var(--card)", borderColor: "var(--accent)", clipPath: "polygon(0 0, 100% 0, 100% calc(100% - 26px), calc(100% - 26px) 100%, 0 100%)" }}>
    <div className="flex items-center gap-2 mb-4">
      <Asterisk size={16} style={{ color: "var(--accent)" }} />
      <p className="text-xs uppercase tracking-[0.25em] font-medium m-0" style={{ color: "var(--accent)" }}>My verdict</p>
    </div>
    <p className="font-serif-display font-light text-2xl md:text-3xl leading-snug mt-0 mb-7" style={{ color: "var(--ink)" }}>{verdict}</p>
    <div className="grid sm:grid-cols-2 gap-6">
      <div>
        <p className="text-xs uppercase tracking-[0.2em] font-medium mb-3" style={{ color: "var(--accent)" }}>Verified</p>
        <ul className="space-y-2 m-0 p-0 list-none">
          {verified.map((item, i) => (
            <li key={i} className="text-sm leading-relaxed flex gap-2.5" style={{ color: "var(--ink)" }}>
              <Check size={15} className="shrink-0 mt-1" style={{ color: "var(--accent)" }} />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </div>
      <div>
        <p className="text-xs uppercase tracking-[0.2em] font-medium mb-3" style={{ color: "var(--bronze)" }}>Still open</p>
        <ul className="space-y-2 m-0 p-0 list-none">
          {open.map((item, i) => (
            <li key={i} className="text-sm leading-relaxed flex gap-2.5" style={{ color: "var(--ink-muted)" }}>
              <CircleDashed size={15} className="shrink-0 mt-1" style={{ color: "var(--bronze)" }} />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  </aside>
);
